/**
 * Pan/zoom-driven viewport synchronisation.
 *
 * Wires a MapLibre ``moveend`` listener that, after the user
 * settles (configurable debounce; 300 ms default), recomputes the
 * map's bounding box and pushes the new viewport to the supplied
 * callback. The callback typically updates the snapshot + SSE
 * viewport state in :class:`Map`, which:
 *
 *   1. Re-keys the :func:`useSnapshot` TanStack Query (key includes
 *      the viewport floats) → fresh snapshot fires for the new bbox.
 *   2. Re-runs the :func:`useSse` ``useEffect`` (viewport is in its
 *      deps) → existing :class:`EventSource` closes; a new one
 *      opens with the new viewport query params; the BFF applies
 *      the new per-client filter.
 *
 * # Why a helper instead of inline ``map.on`` in :file:`Map.tsx`
 *
 * Extraction makes the debounce + bounds-extraction logic unit-
 * testable without booting a real :class:`MapLibreMap` instance —
 * the test injects a recording map (same pattern as
 * :file:`cluster-layer.test.tsx`).
 *
 * # One settled viewport, one fetch (the 2026-10-01 dedupe)
 *
 * The viewport this module emits is **quantised** to a fixed grid
 * (:const:`VIEWPORT_QUANTUM_DEG`, 1e-4°) by :func:`quantizeViewport`, and
 * every viewport-keyed feed (:func:`useSnapshot`, :func:`useTracksFeed`,
 * :func:`useObjectTracksFeed`) quantises again inside its query key + URL.
 * MapLibre's ``getBounds()`` after a camera animation that lands on the same
 * camera (the first-load home ``flyTo``, an inertia settle, a ``resize``)
 * differs from the previous read only in the last digits of the floats —
 * keyed raw, each of those reads re-keyed three TanStack queries and
 * re-opened the SSE :class:`EventSource`. On the ``tracks-demo`` testbed
 * (ADR-016 amendment 2026-10-01) that showed as ~2.5 ``GET
 * /api/query/tracks`` requests per distinct viewport, each a ~70 KB
 * response over a window function on ~24 000 rows. Quantised, the twins
 * collapse to one key; :func:`viewportsEqual` lets :class:`Map` drop the
 * no-op update before it reaches React state at all, so neither the queries
 * nor the SSE subscription see it.
 *
 * # Closes
 *
 * REQ-UI-007 (viewport → BFF — was "partial: M3 opens SSE with the
 * active viewport; pan-driven re-subscribe is M4's concern").
 *
 * Cross-references:
 *   - REQ-UI-007.
 *   - ADR-016 §3 (snapshot orchestration); ADR-016 amendment 2026-10-01
 *     ("Not changed, noted for the UI" — the request amplification this
 *     module's quantisation removes).
 *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md.
 */
import type { SnapshotViewport } from "@/map/snapshot-types";

/**
 * The slice of the MapLibre :class:`Map` surface
 * :func:`installViewportSync` reaches into.
 *
 * Narrowed so tests can inject a recording double without
 * implementing the full :class:`MapLibreMap` Protocol.
 */
export interface ViewportSyncMapLike {
  on(eventName: "moveend", handler: () => void): unknown;
  off(eventName: "moveend", handler: () => void): unknown;
  getBounds(): ViewportBoundsLike;
}

/**
 * Default debounce window for pan/zoom-driven viewport updates.
 *
 * 300 ms is short enough that the new snapshot+SSE re-subscribe
 * lands quickly after the user settles, long enough that mid-pan
 * fling intermediate ``moveend`` events don't thrash the
 * :class:`EventSource` close/open cycle.
 *
 * Per ADR-016 §3 the snapshot-refresh budget is "next paint after
 * pan settles, ≤ 1 second"; 300 ms debounce + ~500 ms BFF p95
 * (REQ-QUERY-007) fits comfortably.
 */
export const DEFAULT_VIEWPORT_SYNC_DEBOUNCE_MS = 300;

/**
 * Clamp a numeric value to ``[lo, hi]``.
 *
 * Module-local helper used to keep emitted viewport bounds within
 * the BFF's accepted ranges (longitude ±180, latitude ±90). See
 * :func:`installViewportSync` for the rationale.
 */
function clamp(value: number, lo: number, hi: number): number {
  return Math.min(Math.max(value, lo), hi);
}

/**
 * Clamp a :type:`SnapshotViewport` to the BFF's accepted bounds
 * (longitude ±180°, latitude ±90°).
 *
 * Exported so the Map's ``onMapReady`` callback can apply the
 * same defensive clamp the moveend handler uses — MapLibre's
 * :meth:`Map.getBounds` can briefly return out-of-range values
 * at the projection edges, and the BFF rejects with HTTP 422 per
 * Pydantic ``ge=-180/le=180`` and ``ge=-90/le=90`` validators on
 * the snapshot + SSE route surfaces.
 *
 * Returns the viewport unchanged when all four bounds are already
 * in range.
 *
 * Cross-references:
 *   - REQ-UI-007 (viewport → BFF coordination).
 *   - Post-#245 rendering-cascade diagnostic: an unclamped
 *     ``onMapReady`` initial bounds value produced 422s before
 *     the moveend handler ever ran.
 */
export function clampViewport(viewport: SnapshotViewport): SnapshotViewport {
  return {
    min_longitude: clamp(viewport.min_longitude, -180.0, 180.0),
    min_latitude: clamp(viewport.min_latitude, -90.0, 90.0),
    max_longitude: clamp(viewport.max_longitude, -180.0, 180.0),
    max_latitude: clamp(viewport.max_latitude, -90.0, 90.0),
  };
}

/**
 * Grid pitch, in degrees, that every viewport-keyed BFF request is snapped
 * to (``1e-4°`` ≈ 11.1 m of latitude; ≤ 11.1 m of longitude).
 *
 * Why this pitch: MapLibre's 512-px tiles make one screen pixel span
 * ``360 / (512 · 2^zoom)`` degrees — a 1e-4° cell is **sub-pixel through
 * zoom 12** (the whole operational range of a 150-Producer picture) and at
 * most 11 m at any zoom above that. The snap is *outward* (west/south
 * floored, east/north ceiled — see :func:`quantizeViewport`), so the
 * requested box always contains the true viewport: the cost is an
 * over-fetch of at most one cell per edge, never a Producer or track
 * missing at the screen edge. A coarser pitch would widen that margin
 * for no further gain — the noise it absorbs is sub-1e-9° (the last
 * digits of a double after a camera animation), ten orders below the
 * cell; a finer one would not change which pans share a key.
 *
 * Cross-references:
 *   - ADR-016 amendment 2026-10-01 (the amplification being removed).
 *   - REQ-UI-007 (viewport → BFF).
 */
export const VIEWPORT_QUANTUM_DEG = 1e-4;

/** Grid cells per degree — the reciprocal of :const:`VIEWPORT_QUANTUM_DEG`. */
const CELLS_PER_DEGREE = 1 / VIEWPORT_QUANTUM_DEG;

/**
 * Tolerance, in grid cells, that keeps the snap idempotent: a bound that is
 * already on the grid (``i / 10 000``) multiplies back to ``i ± ~1e-10``,
 * and without the tolerance ``floor`` would step a further cell outward on
 * every pass. 1e-6 cells is 1e-10°, far below any input noise and far
 * above the multiplication error.
 */
const GRID_EPSILON_CELLS = 1e-6;

/** ``-0`` → ``0`` so a URL never reads ``min_longitude=0`` for one twin and
 *  ``-0`` for the other (``String(-0)`` is ``"0"``, but keep the key honest). */
function normalizeZero(value: number): number {
  return value === 0 ? 0 : value;
}

/** Snap a western / southern bound outward (down) onto the grid. */
function snapDown(value: number): number {
  return normalizeZero(
    Math.floor(value * CELLS_PER_DEGREE + GRID_EPSILON_CELLS) / CELLS_PER_DEGREE,
  );
}

/** Snap an eastern / northern bound outward (up) onto the grid. */
function snapUp(value: number): number {
  return normalizeZero(Math.ceil(value * CELLS_PER_DEGREE - GRID_EPSILON_CELLS) / CELLS_PER_DEGREE);
}

/**
 * Snap a viewport **outward** onto the :const:`VIEWPORT_QUANTUM_DEG` grid
 * and clamp the result to the BFF's accepted ranges.
 *
 * Two viewports that differ only below the grid pitch — the same camera
 * read twice across an animation, a sub-pixel inertia settle — quantise to
 * the **same** four numbers, so a TanStack key built from them is the same
 * key and a URL built from them is the same URL. The function is
 * idempotent (``quantize(quantize(v))`` equals ``quantize(v)``), so a
 * feed may quantise a viewport the Map has already quantised. The clamp
 * runs *after* the snap because ceiling ``180.00006`` lands on
 * ``180.0001``, which the BFF rejects with HTTP 422.
 *
 * Division by ``CELLS_PER_DEGREE`` (rather than multiplication by the
 * quantum) is deliberate: ``i / 10_000`` is the double nearest the
 * four-decimal value, whose shortest round-trip string *is* that value, so
 * the URL carries ``-90.6119`` rather than ``-90.61190000000001``.
 *
 * @param viewport - Any viewport, raw or already quantised.
 * @returns A new viewport on the grid, inside ±180° / ±90°.
 *
 * Cross-references:
 *   - REQ-UI-007; ADR-016 amendment 2026-10-01.
 */
export function quantizeViewport(viewport: SnapshotViewport): SnapshotViewport {
  return clampViewport({
    min_longitude: snapDown(viewport.min_longitude),
    min_latitude: snapDown(viewport.min_latitude),
    max_longitude: snapUp(viewport.max_longitude),
    max_latitude: snapUp(viewport.max_latitude),
  });
}

/**
 * Value equality for two viewports (all four bounds ``===``).
 *
 * :class:`Map` compares the viewport it is about to publish against the
 * last one it published and skips the React state update when they are
 * equal — a new object with the same quantised bounds would otherwise
 * re-run :func:`useSse`'s effect (its dependency is the object) and
 * close / re-open the :class:`EventSource` for nothing.
 */
export function viewportsEqual(a: SnapshotViewport, b: SnapshotViewport): boolean {
  return (
    a.min_longitude === b.min_longitude &&
    a.min_latitude === b.min_latitude &&
    a.max_longitude === b.max_longitude &&
    a.max_latitude === b.max_latitude
  );
}

/**
 * The slice of a MapLibre :class:`LngLatBounds` the viewport readers use.
 * Narrowed (like :type:`ViewportSyncMapLike`) so tests inject a plain
 * object.
 */
export interface ViewportBoundsLike {
  getWest(): number;
  getSouth(): number;
  getEast(): number;
  getNorth(): number;
}

/**
 * Read a MapLibre bounds object into the viewport the BFF feeds are keyed
 * on: clamped (see :func:`clampViewport`) **and** quantised (see
 * :func:`quantizeViewport`).
 *
 * The single reader for both viewport sources in :class:`Map` — the
 * ``onMapReady`` first read and the debounced ``moveend`` path below — so
 * the two can never disagree about clamp order or grid pitch.
 *
 * @param bounds - ``map.getBounds()`` or a test double.
 */
export function viewportFromBounds(bounds: ViewportBoundsLike): SnapshotViewport {
  return quantizeViewport(
    clampViewport({
      min_longitude: bounds.getWest(),
      min_latitude: bounds.getSouth(),
      max_longitude: bounds.getEast(),
      max_latitude: bounds.getNorth(),
    }),
  );
}

/**
 * Install a debounced ``moveend`` listener that calls ``onChange``
 * with the map's current bounds whenever the user finishes
 * panning or zooming.
 *
 * @param map - The MapLibre map (or test double).
 * @param onChange - Receiver for the new viewport. Typically wired
 *   to ``setSnapshotViewport`` + ``setSseViewport`` in :class:`Map`.
 * @param debounceMs - Idle window before firing ``onChange``.
 *   Defaults to :const:`DEFAULT_VIEWPORT_SYNC_DEBOUNCE_MS`.
 * @returns A cleanup function. Callers MUST invoke it on unmount
 *   (or when the map instance changes) to remove the listener and
 *   cancel any pending timer.
 */
export function installViewportSync(
  map: ViewportSyncMapLike,
  onChange: (viewport: SnapshotViewport) => void,
  debounceMs: number = DEFAULT_VIEWPORT_SYNC_DEBOUNCE_MS,
): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const handler = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      timer = null;
      // Clamp + quantise through the shared reader. The clamp: MapLibre's
      // ``renderWorldCopies: false`` (set in :func:`useMap`) keeps the
      // viewport inside one world copy in normal operation, but
      // ``getBounds`` can briefly return out-of-range values during
      // fling/inertia or at projection edges, and the BFF rejects an
      // out-of-range bbox with HTTP 422 (Pydantic ge=-180/le=180,
      // ge=-90/le=90) — per the 2026-05-27 rendering cascade, issue #1.
      // The quantisation: a settle on the same camera must not read as a
      // new viewport (module docstring §"One settled viewport, one fetch").
      onChange(viewportFromBounds(map.getBounds()));
    }, debounceMs);
  };

  map.on("moveend", handler);

  return () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    map.off("moveend", handler);
  };
}
